// frontend_react/src/components/CertificationModal.jsx

/*
  Ce composant gère les deux types de certification:
  -----------------------------------------------------------------------------
  •B2C(NF525_officielle):   props.target.nf525 === true
      - déclenche /async/fec   (pas d'UUID nécessaire)
  •B2B(NF525‑like)       :   nf525 absent ou = false
      - déclenche /admin/replenishment/<uuid>/certify

  Props obligatoire:
    open          : bool – affichage modal
    onClose       : () => void
    target        : {
                     uuid: string,          // toujours fourni
                     supplier?: string,     // B2B uniquement
                     total?: number,        // TTC
                     certified?: boolean,
                     nf525?: boolean        // true = B2C, false/undefined = B2B
                   }
    onCertified   : (payload) => void  – callback après succès
*/

import React, { useState } from "react";
import styled from "styled-components";
import { certifyNF525 } from "../services/certificateApi";
import { certifyReplenishment } from "../services/replenishmentApi";
import { Button } from "../ui";
import { useAdminScope } from "../context/AdminScopeContext";

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: ${({ open }) => (open ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Modal = styled.div`
  background: #fff;
  padding: 2rem;
  border-radius: 8px;
  width: 100%;
  max-width: 500px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 0.8rem;
`;

const Badge = styled.span`
  display: inline-block;
  background: #28a745;
  color: #fff;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  font-size: 0.8rem;
  margin-left: 0.5rem;
`;

export default function CertificationModal({ open, onClose, target, onCertified }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { selectedCompanyUuid } = useAdminScope();
  if (!open || !target) return null;

  const isNF525 = Boolean(target.nf525);
  const title = isNF525
    ? `Certification Ticket Z (NF525)`
    : `Certification réassort fournisseur (NF525‑like)`;

  const handleCertify = async () => {
    setLoading(true);
    setError("");
    try {
      const res = isNF525
        ? await certifyNF525() // endpoint async/fec
        : await certifyReplenishment(target.uuid, {
          companyUuid: selectedCompanyUuid,
          });
      onCertified(res);
    } catch (err) {
      setError(err.response?.data?.message || "Erreur de certification");
    } finally {
      setLoading(false);
      onClose();
    }
  };

  return (
    <Overlay open={open} data-testid="overlay" onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <h3>{title}</h3>

        {/* Infos dynamiques */}
        {!isNF525 && (
          <Row>
            <span>Fournisseur:</span>
            <strong>{target.supplier || "–"}</strong>
          </Row>
        )}

        {target.total !== undefined && (
          <Row>
            <span>Total_TTC:</span>
            <strong>{(target.total || 0).toFixed(2)}€</strong>
          </Row>
        )}

        {target.certified && <Badge data-testid="badge">Certifié</Badge>}
        {error && <p style={{ color: "red" }}>{error}</p>}

        <div style={{ textAlign: "right", marginTop: "1.5rem" }}>
          <Button onClick={onClose} style={{ marginRight: "0.5rem" }} disabled={loading}>
            Annuler
          </Button>
          {!target.certified && (
            <Button onClick={handleCertify} disabled={loading}>
              {loading ? "En cours…" : "Envoyer pour certification"}
            </Button>
          )}
        </div>
      </Modal>
    </Overlay>
  );
}
